/**
 * TimelineBookmarkLayer Component
 *
 * Renders expandable bookmarks for expanded crop rows.
 * Bookmarks extend below phase rectangles with labels and durations.
 */

import React, { useCallback } from 'react'
import {
  useTimelineContextDispatch,
  setHoveredPhaseKey as setHoveredPhaseKeyAction,
  setExpandedIds as setExpandedIdsAction,
} from '../../context/timeline-context'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import { useTimelineInteractionContext } from '../../context/timeline-interaction-context'
import { useOrgHoverContext } from '~/common/context/organization/org-context'
import { useTimelineStyles } from '../../../hooks/use-timeline-styles'
import { Bookmark } from './bookmark'
import { calculateBookmarkWidth } from '../../../utils/bookmark-utils'
import {
  EXPANDED_ROW_HEIGHT,
  EXPANDED_ROW_HEIGHT_GAP,
  VERTICAL_DAY_TRACKER_SPACE,
} from '../../timeline-constants'

// Stable style object — defined once at module level, never reallocated
const ISOLATION_STYLE = { isolation: 'isolate' }

/**
 * TimelineBookmarkLayer - Expandable bookmarks for expanded rows
 *
 * Only renders for expanded crops. Bookmarks are sorted so active/hovered
 * bookmarks render on top.
 *
 * @param {Object} props
 * @param {Array} props.crops - Transformed crop data
 * @param {boolean} props.forceAllExpand - Force all crops expanded
 */
export const TimelineBookmarkLayer = React.memo(({ crops, forceAllExpand, interactive, onPanStart }) => {
  const {
    expandedIds,
    activePhaseKey,
    weekWidth,
    getRowY,
    viewStrategy,
  } = useTimelineStaticContext()
  const { hoveredPhaseKey } = useTimelineInteractionContext()

  const dispatch = useTimelineContextDispatch()
  const { hoveredOrg } = useOrgHoverContext()

  const { getPhaseStyle } = useTimelineStyles()

  // Stable dispatch handlers — wrapped in useCallback so identity is stable across renders
  const handleHoverPhase = useCallback((phaseKey) => {
    dispatch(setHoveredPhaseKeyAction({ hoveredPhaseKey: phaseKey }))
  }, [dispatch])

  const handleClickPhase = useCallback((crop) => () => {
    if (interactive || crop.interactive) return
    const newExpandedIds = expandedIds.filter(i => i !== crop.id)
    dispatch(setExpandedIdsAction({ expandedIds: newExpandedIds }))
  }, [interactive, expandedIds, dispatch])

  return (
    <>
      {crops.map((crop) => {
        if (crop.isOrgTaskRow) return null  // Org task rows have no phases

        const isExpanded = forceAllExpand || expandedIds.includes(crop.id)
        if (!isExpanded) return null  // Only render bookmarks for expanded crops

        const rowY = getRowY(crop.id) + VERTICAL_DAY_TRACKER_SPACE * 8

        // Calculate row height: double when showing both phases and tasks
        const baseRowH = EXPANDED_ROW_HEIGHT
        const rowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? baseRowH * 2 : baseRowH

        const barH = EXPANDED_ROW_HEIGHT - EXPANDED_ROW_HEIGHT_GAP

        // Calculate sub-row height when both phases and tasks shown
        const subRowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? rowH / 2 : rowH

        // Position in top half when both, otherwise center in full row
        const y = viewStrategy === 'both' && !crop.isOrgTaskRow
          ? rowY + (subRowH / 2) - (barH / 2)
          : rowY + (rowH / 2) - (barH / 2)

        const isParentOrgHovered = crop.organization_id === hoveredOrg?.id
        const cropInteractive = interactive || crop.interactive
        const handleMouseDownPhase = cropInteractive
          ? (e) => { e.stopPropagation(); e.preventDefault(); onPanStart(e.clientX) }
          : undefined

        return (
          <g key={`bookmarks-${crop.id}`} style={ISOLATION_STYLE}>
            {crop.phases
              .map((phase, pidx) => {
                const s = getPhaseStyle(phase.type)
                const x = phase.start * weekWidth
                const anchorWidth = phase.len * weekWidth
                const phaseKey = `${crop.id}-${pidx}`
                const duration = phase.duration || Math.round(phase.len * 7)
                const label = phase.label || s.label
                const bookmarkWidth = calculateBookmarkWidth(label, duration, anchorWidth)

                return {
                  phase,
                  pidx,
                  s,
                  x,
                  anchorWidth,
                  phaseKey,
                  duration,
                  label,
                  bookmarkWidth,
                  y,
                  barH
                }
              })
              .sort((a, b) => {
                // Render active/hovered bookmark last (on top)
                if (a.phaseKey === activePhaseKey || a.phaseKey === hoveredPhaseKey) return 1
                if (b.phaseKey === activePhaseKey || b.phaseKey === hoveredPhaseKey) return -1
                return 0
              })
              .map(({ phaseKey, x, anchorWidth, bookmarkWidth, y, barH, s, duration }) => (
                <Bookmark
                  key={phaseKey}
                  x={x}
                  y={y}
                  anchorWidth={anchorWidth}
                  bookmarkWidth={bookmarkWidth}
                  totalHeight={barH}
                  style={s}
                  duration={duration}
                  isActive={activePhaseKey === phaseKey}
                  isHovered={hoveredPhaseKey === phaseKey}
                  isParentOrgHovered={isParentOrgHovered}
                  onHover={handleHoverPhase}
                  onClick={handleClickPhase(crop)}
                  onMouseDown={handleMouseDownPhase}
                  itemKey={phaseKey}
                />
              ))}
          </g>
        )
      })}
    </>
  )
})
